
import { useQuery } from "@tanstack/react-query";
import { userApiClient, businessApiClient, staffApiClient, activityApiClient } from "@/api";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export const useDashboardData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Using React Query for better caching and deduplication
  const { data: userId, isLoading: isUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: userApiClient.getCurrentUser,
    staleTime: 300000, // 5 minutes
    retry: 1,
    meta: {
      onError: () => {
        toast({
          title: "Authentication required",
          description: "Please sign in to view your dashboard.",
          variant: "destructive",
        });
        navigate("/auth");
      }
    }
  });
  
  // Only fetch business if we have a userId
  const { data: business, isLoading: isBusinessLoading } = useQuery({
    queryKey: ['business', userId],
    queryFn: () => businessApiClient.fetchBusinessByOwnerId(userId!),
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
    retry: 1
  });
  
  // Fetch subscription details using the business ID
  const { data: subscription, isLoading: isSubscriptionLoading } = useQuery({
    queryKey: ['subscription', business?.id],
    queryFn: () => businessApiClient.fetchSubscriptionDetails(business?.id!),
    enabled: !!business?.id,
    staleTime: 300000, // 5 minutes
    retry: 1
  });
  
  // Fetch staff count - specifically filtering for ACTIVE staff
  const { data: staffList, isLoading: isStaffLoading } = useQuery({
    queryKey: ['staff', business?.id],
    queryFn: () => staffApiClient.fetchAllStaff(business?.id!),
    enabled: !!business?.id,
    staleTime: 60000, // 1 minute - shorter for more frequent updates
    retry: 1
  });
  
  // Fetch activities
  const { data: activities, isLoading: isActivitiesLoading } = useQuery({
    queryKey: ['activities'],
    queryFn: () => activityApiClient.fetchActivities(undefined, 100),
    enabled: !!userId,
    staleTime: 60000, // 1 minute
    retry: 1
  });
  
  // Count only ACTIVE staff - filter out terminated/inactive staff
  const activeStaffCount = staffList ? 
    staffList.filter(staff => staff.is_active !== false).length : 
    0;
  
  const tasksCount = activities?.length || 0;
  
  const isLoading = isUserLoading || isBusinessLoading || isSubscriptionLoading || isStaffLoading || isActivitiesLoading;
  
  return {
    userId,
    business,
    subscription,
    staffList,
    activities,
    activeStaffCount,
    tasksCount,
    isUserLoading,
    isBusinessLoading,
    isSubscriptionLoading,
    isStaffLoading,
    isActivitiesLoading,
    isLoading
  };
};
